import React, { useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { BaseSelect } from 'ui_system/forms/BaseSelect';
import SearchInput from 'ui_system/inputs/search/SearchInput';
import GreenButton from 'ui_system/buttons/ThemeButton';
import ContentLoader from 'ui_system/utilities/ContentLoader';

import { Theme } from 'ui_system/theme';
import { t } from 'services/translation';
import { useDidUpdateEffect } from 'ui_system/hooks/useDidUpdateEffect';

import { Api } from './locationApi';
import { LocationInfo, StoresWithin } from './shared';

const SearchContainer = styled.div`
  font-size: 14px;
  line-height: 1.33;
`;

const Top = styled.div`
  margin: 0 40px;
  padding-top: 40px;
  padding-bottom: ${(props) => (props.newSearch ? '40px' : '20px')};
`;

const SearchButton = styled(GreenButton)`
  height: 44px;
  align-self: center;
  justify-content: center;
  font-size: 16px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    font-size: 16px;
    width: 100%;
  }
`;

const Inputs = styled.div`
  display: flex;
  justify-content: space-between;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    flex-direction: column;
  }
`;

const StyledSearchInput = styled(SearchInput)`
  max-width: 333px;
  input {
    height: 100%;
  }
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    max-width: initial;
    margin-bottom: 20px;
  }
`;

const SelectWrapper = styled.div`
  width: 150px;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    width: initial;
    margin-bottom: 20px;
  }
`;

const Title = styled.div`
  font-size: 16px;
  margin-bottom: 16px;
  font-weight: 600;
  padding-right: 20px;
`;

const Middle = styled.div`
  margin: 0 40px;
  margin-bottom: ${(props) => (props.noLocations ? '30px' : '10px')};
`;

const Border = styled.div`
  margin: 0 40px;
  height: 1px;
  background: ${Theme.colors.lightLine};
`;

const LocationList = styled.div`
  padding: 0 40px;
  padding-top: 10px;
  max-height: 390px;
  overflow-y: auto;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    max-height: calc(100vh - 310px);
  }
`;

const LocationItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 10px 20px;
  &:hover {
    background: ${Theme.colors.selectionBlue};
  }
`;

const LocationListLoading = styled.div`
  padding: 0 40px;
  padding-top: 10px;
  height: 390px;
`;

const LocationItemLoading = styled.div`
  padding: 10px 20px;
`;

const RightArrow = styled.i`
  border: solid ${Theme.colors.line};
  border-width: 0 2px 2px 0;
  padding: 4px;
  transform: rotate(-45deg);
`;

function LoadingPlaceholder() {
  return (
    <React.Fragment>
      <Middle>
        <ContentLoader height={12} width={200} />
      </Middle>
      <Border />
      <LocationListLoading>
        {[...Array(5)].map((_, i) => {
          return (
            <LocationItemLoading key={i}>
              <div>
                <ContentLoader height={12} width={200} />
              </div>
              <div>
                <ContentLoader height={12} width={100} />
              </div>
              <div>
                <ContentLoader height={12} width={150} />
              </div>
            </LocationItemLoading>
          );
        })}
      </LocationListLoading>
    </React.Fragment>
  );
}

const unitsAbbrev = {
  kilometers: 'km',
  miles: 'mi',
};

export function LocationSearch(props) {
  const {
    onSelect,
    zipCode,
    locations,
    distance,
    distanceOptions,
    userId,
    tourId,
    pluginSlug,
    units,
  } = props;

  const [_distance, setDistance] = useState(distance);
  const [_locations, setLocations] = useState(locations);
  const [search, setSearch] = useState(zipCode);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(!!zipCode);

  const [displayValues, setDisplayValues] = useState({ distance, zipCode });

  const distancesWithLabel = distanceOptions.map((option) => ({
    value: option,
    label: `${t('WITHIN')} ${option} ${unitsAbbrev[units]}`,
  }));

  const distanceOption = distancesWithLabel.find(
    (val) => val.value === _distance
  );

  const handleSearch = () => {
    async function searchLocations() {
      const params = {
        query: search,
        radius: _distance,
        user_id: userId,
        tour_id: tourId,
        plugin_slug: pluginSlug,
      };

      try {
        setSearched(true);
        setLoading(true);
        const response = await Api.querySearch(params);
        const { locations: newLocations } = response.data;

        setLocations(newLocations.map((loc) => ({ value: loc })));
      } catch (err) {
        setLocations([]);
      }
      setDisplayValues({
        distance: _distance,
        zipCode: search,
      });
      setLoading(false);
    }

    if (search) {
      // hit api to find locations
      searchLocations();
    }
  };

  useDidUpdateEffect(() => {
    handleSearch();
  }, [_distance]);

  function renderList() {
    if (loading) {
      return <LoadingPlaceholder />;
    }
    return (
      <React.Fragment>
        {(searched || _locations.length > 0) && (
          <Middle noLocations={_locations.length === 0}>
            <StoresWithin
              {...{
                numStores: _locations.length,
                distance: displayValues.distance,
                zipCode: displayValues.zipCode,
                units,
              }}
            />
          </Middle>
        )}

        {_locations.length > 0 && (
          <React.Fragment>
            <Border />
            <LocationList>
              {_locations.map((location, i) => {
                return (
                  <LocationItem
                    className="LocationSearch-LocationItem"
                    data-testid="location-item"
                    onClick={() =>
                      onSelect({
                        location: {
                          value: location.value,
                          label:
                            location.value.shopping_center ||
                            location.value.address ||
                            location.value.city,
                        },
                        locations: _locations,
                        distance: displayValues.distance,
                        zipCode: displayValues.zipCode,
                      })
                    }
                    key={i}
                  >
                    <LocationInfo {...location.value} units={units} />
                    <RightArrow />
                  </LocationItem>
                );
              })}
            </LocationList>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  return (
    <SearchContainer>
      <Top newSearch={!searched && _locations.length === 0}>
        <Title>{t('FIND_A_STORE_BY_ZIP_CODE_OR_CITY_AND_STATE')}</Title>
        <Inputs>
          <StyledSearchInput
            className="LocationSearch-SearchInput"
            dataTestId="enterZipCodeOrCityAndState"
            value={search}
            onChange={setSearch}
            onEnter={handleSearch}
            onClear={() => setSearch('')}
            placeholder={t('ENTER_ZIP_CODE_OR_CITY_AND_STATE')}
          />
          <SelectWrapper className="LocationSearch-DistanceInput">
            <BaseSelect
              value={distanceOption}
              onChange={(sel) => setDistance(sel.value)}
              options={distancesWithLabel}
              styles={{
                singleValue: (state) => ({ whiteSpace: 'initial' }),
              }}
            />
          </SelectWrapper>
          <SearchButton
            className="brand-bg brand-border LocationSearch-SearchButton"
            data-testid="location-search-button"
            onClick={handleSearch}
          >
            {t('SEARCH')}
          </SearchButton>
        </Inputs>
      </Top>
      {renderList()}
    </SearchContainer>
  );
}

LocationSearch.propTypes = {
  onSelect: PropTypes.func.isRequired,
  zipCode: PropTypes.string.isRequired,
  locations: PropTypes.array.isRequired,
  distance: PropTypes.object.isRequired,
  distanceOptions: PropTypes.array.isRequired,
  userId: PropTypes.number.isRequired,
  units: PropTypes.string.isRequired,
  tourId: PropTypes.number,
  pluginSlug: PropTypes.string,
};
